import React, { useState, useEffect } from 'react';
import ReactPaginate from 'react-js-pagination';
import { Col, Row } from 'reactstrap';

interface PaginationProps<T> {
    data: T[]; // Data array to be paginated
    itemsPerPage: number; // Number of items per page
    onPageChange: (selectedPage: number) => void; // Callback function for page change
    activePage?: number; // Currently active page
    pageRangeDisplayed?: number; // Number of pages to display in the pagination
    length: number;
}

function Pagination<T>({ data, length, itemsPerPage, onPageChange, activePage = 1, pageRangeDisplayed = 5 }: PaginationProps<T>) {
    const [currentPage, setCurrentPage] = useState(activePage);

    useEffect(() => {
        // Reset to first page when data changes
        setCurrentPage(activePage);
    }, [data, itemsPerPage, activePage]);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        onPageChange(pageNumber);
    };

    const handleStaticDataLen = () => {
        // Calculate the starting index of the records on the current page
        const startIndex = (currentPage - 1) * itemsPerPage;
        // Calculate the ending index of the records on the current page
        let endIndex = startIndex + itemsPerPage;
        // Adjust the endIndex if it exceeds the total number of records
        if (endIndex > length) {
            endIndex = length;
        }
        // Calculate the number of records displayed on the current page
        const recordsOnThisPage = endIndex - startIndex;
        return recordsOnThisPage;
    };

    const dataLen = () => {
        const startCount = (currentPage - 1) * itemsPerPage + 1;

        let endCount = currentPage * itemsPerPage;
        if (endCount > length) {
            endCount = length;
        }
        return `${startCount} to ${endCount}`;
    };

    return (
        <Row className="p-3">
            <Col className="d-flex justify-content-start">
                <div className="text-muted">
                    Showing <span className="fw-semibold">{dataLen()}</span> of <span className="fw-semibold">{length}</span> Results
                </div>
            </Col>
            <Col className="d-flex justify-content-end">
                <ReactPaginate
                    activePage={currentPage}
                    itemsCountPerPage={itemsPerPage}
                    totalItemsCount={length}
                    pageRangeDisplayed={pageRangeDisplayed}
                    onChange={handlePageChange}
                    prevPageText="Previous"
                    nextPageText="Next"
                    firstPageText="First"
                    lastPageText="Last"
                    linkClass="page-link"
                    itemClass="page-item"
                    innerClass="pagination pagination-separated pagination-sm mb-0"
                />
            </Col>
        </Row>
    );
}

export default Pagination;
